import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Mail, Server, Save, Loader2, Send, CheckCircle2,
  AlertCircle, Eye, EyeOff, Shield, Globe, Bell, Plus, Trash2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [savingNotify, setSavingNotify] = useState(false);

  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    use_ssl: false,
    site_url: '',
  });

  useEffect(() => { loadSettings(); loadNotifyEmails(); }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/settings/smtp`);
      setSmtp(res.data);
      setConfigured(res.data.configured);
    } catch (err) {
      toast.error('Failed to load SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!smtp.smtp_host || !smtp.smtp_user) {
      toast.error('SMTP host and username are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/settings/smtp`, smtp);
      toast.success(res.data.message);
      setConfigured(true);
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Enter an email address to send the test to');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/admin/settings/smtp/test`, { to_email: testEmail });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed');
    } finally {
      setTesting(false);
    }
  };

  const loadNotifyEmails = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/settings/notifications`);
      setNotifyEmails(res.data.emails?.length ? res.data.emails : ['']);
    } catch {
      setNotifyEmails(['']);
    }
  };

  const handleSaveNotify = async () => {
    setSavingNotify(true);
    try {
      const cleaned = notifyEmails.filter(e => e.trim());
      const res = await axios.put(`${API_URL}/admin/settings/notifications`, { emails: cleaned });
      toast.success(res.data.message);
      setNotifyEmails(res.data.emails.length ? res.data.emails : ['']);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notification emails');
    } finally {
      setSavingNotify(false);
    }
  };

  const addNotifyEmail = () => setNotifyEmails(prev => [...prev, '']);
  const removeNotifyEmail = (idx) => setNotifyEmails(prev => prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx));
  const updateNotifyEmail = (idx, val) => setNotifyEmails(prev => { const u = [...prev]; u[idx] = val; return u; });

  const set = (field, value) => setSmtp(prev => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <PageHeader
          title="Settings"
          description="Configure email, notifications, and system settings"
          breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Settings' }]}
        />

        {/* Status Banner */}
        <div className={`flex items-center gap-3 p-4 rounded-xl mb-8 ${configured ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="smtp-status-banner">
          {configured ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800">Email is configured</p>
                <p className="text-xs text-emerald-600">Password resets, vendor notifications, and submission alerts will be sent via email.</p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Email not configured</p>
                <p className="text-xs text-amber-600">Configure SMTP settings below to enable email notifications.</p>
              </div>
            </>
          )}
        </div>

        {/* SMTP Settings */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>SMTP Configuration</h2>
              <p className="text-sm text-slate-500">Email server settings for sending notifications</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label>SMTP Host</Label>
              <Input
                value={smtp.smtp_host}
                onChange={e => set('smtp_host', e.target.value)}
                placeholder="e.g., mail.yourdomain.com or smtp.gmail.com"
                data-testid="smtp-host"
              />
            </div>

            <div className="space-y-1.5">
              <Label>SMTP Port</Label>
              <Input
                type="number"
                value={smtp.smtp_port}
                onChange={e => set('smtp_port', parseInt(e.target.value) || 587)}
                placeholder="587"
                data-testid="smtp-port"
              />
              <p className="text-xs text-slate-400">587 for TLS, 465 for SSL</p>
            </div>

            <div className="space-y-1.5">
              <Label>Username / Email</Label>
              <Input
                value={smtp.smtp_user}
                onChange={e => set('smtp_user', e.target.value)}
                placeholder="you@yourdomain.com"
                data-testid="smtp-user"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={smtp.smtp_password}
                  onChange={e => set('smtp_password', e.target.value)}
                  placeholder="Your SMTP password"
                  className="pr-10"
                  data-testid="smtp-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>From Email (optional)</Label>
              <Input
                value={smtp.from_email}
                onChange={e => set('from_email', e.target.value)}
                placeholder="noreply@yourdomain.com (defaults to username)"
                data-testid="smtp-from"
              />
            </div>

            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={smtp.use_ssl}
                onCheckedChange={v => {
                  set('use_ssl', v);
                  if (v && smtp.smtp_port === 587) set('smtp_port', 465);
                  if (!v && smtp.smtp_port === 465) set('smtp_port', 587);
                }}
                data-testid="smtp-ssl"
              />
              <div>
                <Label className="mb-0">Use SSL</Label>
                <p className="text-xs text-slate-400">Enable for port 465 (implicit SSL)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Site Domain */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center">
              <Globe className="h-5 w-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Site Domain</h2>
              <p className="text-sm text-slate-500">Base URL used in email links (password reset, vendor invites)</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Site URL</Label>
            <Input
              value={smtp.site_url}
              onChange={e => set('site_url', e.target.value)}
              placeholder="https://yourdomain.com"
              data-testid="site-url-input"
            />
            <p className="text-xs text-slate-400">Leave blank to use the default preview URL. Set this to your production domain when deploying.</p>
          </div>
        </div>

        {/* Shared Save Button */}
        <div className="flex justify-end mb-6">
          <Button onClick={handleSave} disabled={saving} className="btn-primary px-6" data-testid="save-smtp-btn">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : <><Save className="h-4 w-4 mr-2" />Save SMTP & Domain Settings</>}
          </Button>
        </div>

        {/* Notification Emails */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="notification-emails-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Notification Emails</h2>
              <p className="text-sm text-slate-500">These addresses receive alerts for contact inquiries and submission completions</p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            {notifyEmails.map((email, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={e => updateNotifyEmail(idx, e.target.value)}
                  placeholder="team@yourcompany.com"
                  className="flex-1"
                  data-testid={`notify-email-${idx}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-700 h-9 w-9 p-0 flex-shrink-0"
                  onClick={() => removeNotifyEmail(idx)}
                  data-testid={`remove-notify-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addNotifyEmail} className="gap-1.5" data-testid="add-notify-email-btn">
              <Plus className="h-3.5 w-3.5" /> Add Email
            </Button>
          </div>

          <div className="flex justify-end mt-4 pt-4 border-t border-slate-100">
            <Button onClick={handleSaveNotify} disabled={savingNotify} className="btn-primary px-6 gap-2" data-testid="save-notify-btn">
              {savingNotify ? <><Loader2 className="h-4 w-4 animate-spin" />Saving...</> : <><Save className="h-4 w-4" />Save Notification Emails</>}
            </Button>
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Send className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Test Email</h2>
              <p className="text-sm text-slate-500">Send a test email to verify your configuration</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="Enter email address to send test to"
              className="flex-1"
              data-testid="test-email-input"
            />
            <Button
              onClick={handleTest}
              disabled={testing || !configured}
              variant="outline"
              className="gap-2"
              data-testid="send-test-btn"
            >
              {testing ? <><Loader2 className="h-4 w-4 animate-spin" />Sending...</> : <><Send className="h-4 w-4" />Send Test</>}
            </Button>
          </div>
          {!configured && (
            <p className="text-xs text-amber-600 mt-2">Save your SMTP settings first before testing.</p>
          )}
        </div>

        {/* Help */}
        <div className="mt-8 bg-slate-50 rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-5 w-5 text-slate-500" />
            <h3 className="font-medium text-slate-700">Common SMTP Settings</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border border-slate-100">
              <p className="font-medium text-slate-800 mb-1">Gmail</p>
              <p className="text-xs text-slate-500">Host: smtp.gmail.com<br/>Port: 587 (TLS)<br/>Use App Password</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-100">
              <p className="font-medium text-slate-800 mb-1">Microsoft 365</p>
              <p className="text-xs text-slate-500">Host: smtp.office365.com<br/>Port: 587 (TLS)<br/>Use account password</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-100">
              <p className="font-medium text-slate-800 mb-1">Custom IMAP/SMTP</p>
              <p className="text-xs text-slate-500">Host: mail.yourdomain.com<br/>Port: 587 (TLS) or 465 (SSL)<br/>Use email credentials</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
